import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ChevronRight,
  HelpCircle,
} from "lucide-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import DatePicker from "@/components/ui/date-input";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AutoComplete } from "@/components/ui/autocomplete";

interface ReportAnchorProps {
  title: string;
  link: string;
  onClick?: () => void;
}
export type Option = Record<"value" | "label", string> & Record<string, string>;
interface DatePickerState {
  type: "single" | "range" | null;
  startDate?: Date;
  endDate?: Date;
  selectedDate?: Date;
  link: string | null;
}

const ReportAnchor: React.FC<ReportAnchorProps> = ({
  title,
  link,
  onClick,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      className="text-sm bg-popover rounded-lg cursor-pointer max-w-lg"
      onClick={handleClick}
    >
      <div className="px-6 py-3 flex items-center justify-between">
        <p>{title}</p>
        <ChevronRight size={18} />
      </div>
    </div>
  );
};

const STATIC_REPORT_OPTIONS: Option[] = [
  {
    label: "Expiry List",
    value: "expiry-list",
    url: "/branch/reports/expiry-list/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Void Sales",
    value: "void-sales",
    url: "/branch/sales_history?type=VOID",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Refunded Sales",
    value: "refunded-sales",
    url: "/branch/sales_history?type=FULLY_REFUNDED",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Profit & Loss",
    value: "pl-report",
    url: "/branch/reports/pl-report/",
    requires_start_date: "true",
    requires_end_date: "true",
  },
  {
    label: "Supplier Profitability",
    value: "supplier-profitability",
    url: "/branch/reports/supplier-profitability/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Product Profitability",
    value: "product-profitablity",
    url: "/branch/reports/product-profitablity/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Customer Wise Revenue",
    value: "customer-wise-revenue",
    url: "/branch/reports/customer-wise-revenue/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Sales Summary",
    value: "sales-summary",
    url: "/branch/reports/sales-summary/",
    requires_start_date: "true",
    requires_end_date: "false",
  },
  {
    label: "Sales Per Item",
    value: "sales-per-item",
    url: "/branch/reports/sales-per-item/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Out of Stock",
    value: "out-of-stock",
    url: "/branch/reports/out-of-stock/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Shift History",
    value: "shift-history",
    url: "/branch/reports/shift-history/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  // Inventory
  {
    label: "Available Inventory",
    value: "available-inventory",
    url: "/branch/inventory/available-inventory/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Medical Master",
    value: "medical-master",
    url: "/branch/inventory/medical-master/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Non Medical Master",
    value: "non-medical-master",
    url: "/branch/inventory/non-medical-master/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Pending Acceptance Inventory",
    value: "pending-acceptance-inventory",
    url: "/branch/inventory/pending-acceptance-inventory/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  // Item Reorder Reports
  {
    label: "Fast Moving Items",
    value: "fast-moving-items",
    url: "/branch/reports/fast-moving-items/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Slow Moving Items",
    value: "slow-moving-items",
    url: "/branch/reports/slow-moving-items/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Items Sales Velocity",
    value: "items-sales-velocity", 
    url: "/branch/reports/item-sales-velocity/",
    requires_start_date: "false",
    requires_end_date: "false",
  },
  {
    label: "Items Reorder Report",
    value: "items-reorder-report",
    url: "/branch/reports/items-reorder-report/",
    requires_start_date: "false",
    requires_end_date: "false",
  }
];

const ViewReports = () => {
  const navigate = useNavigate();
  const [datePickerState, setDatePickerState] = useState<DatePickerState>({
    type: null,
    link: null,
    startDate: dayjs().add(-1, "months").toDate(),
    endDate: dayjs().toDate(),
    selectedDate: dayjs().toDate(),
  });
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [helpDrawerVisible, setHelpDrawerVisible] = useState(false);
  const [searchReport, setSearchReport] = useState<string>("");
  const [selectedReport, setSelectedReport] = useState<Option | undefined>();
  const [reportOptions, setReportOptions] = useState(STATIC_REPORT_OPTIONS);

  const handleReportAnchorClick = (
    link: string,
    requires_end_date: boolean,
    requires_start_date: boolean,
    detected_start_date?: string,
    detected_end_date?: string
  ) => {
    if (requires_end_date || requires_start_date) {
      let type: "single" | "range" | null = null;
      if (requires_end_date && requires_start_date) {
        type = "range";
        if (
          detected_end_date &&
          detected_start_date &&
          detected_start_date !== "" &&
          detected_end_date !== ""
        ) {
          navigate({
            to: `${link}?startDate=${detected_start_date}&endDate=${detected_end_date}`,
          });
          return;
        }
      } else if (requires_start_date && !requires_end_date) {
        type = "single";
        if (detected_start_date && detected_start_date !== "") {
          navigate({
            to: `${link}?date=${detected_start_date}`,
          });
          return;
        }
      }
      setDatePickerState({
        type,
        link,
        startDate: dayjs().add(-1, "months").toDate(),
        endDate: dayjs().toDate(),
        selectedDate: dayjs().toDate(),
      });
      setSelectedReport({
        label: link,
        value: link,
        url: link,
        requires_start_date: requires_start_date ? "true" : "false",
        requires_end_date: requires_end_date ? "true" : "false",
      } as Option);
      setDatePickerVisible(true);
    } else {
      navigate({ to: link });
    }
  };

  const handleValueChange = (value: Option | null) => {
    setSelectedReport(value || undefined);
    if (value) {
      const requiresStart = value.requires_start_date === "true";
      const requiresEnd = value.requires_end_date === "true";
      handleReportAnchorClick(value.url, requiresEnd, requiresStart);
    }
  };

  const handleApplyDates = () => {
    setDatePickerVisible(false);
    // Only proceed if a report is selected and a link is available
    if (selectedReport && datePickerState.link) {
      const requiresStart = selectedReport.requires_start_date === "true";
      const requiresEnd = selectedReport.requires_end_date === "true";
      if (datePickerState.type === "range" && requiresStart && requiresEnd) {
        // Range: both start and end date required
        const start = datePickerState.startDate ? dayjs(datePickerState.startDate).format("YYYY-MM-DD") : "";
        const end = datePickerState.endDate ? dayjs(datePickerState.endDate).format("YYYY-MM-DD") : "";
        if (start && end) {
          navigate({ to: `${datePickerState.link}?startDate=${start}&endDate=${end}` });
        }
      } else if (datePickerState.type === "single" && requiresStart && !requiresEnd) {
        // Single: only start date required
        const date = datePickerState.selectedDate ? dayjs(datePickerState.selectedDate).format("YYYY-MM-DD") : "";
        if (date) {
          navigate({ to: `${datePickerState.link}?date=${date}` });
        }
      }
    }
    setDatePickerState({ type: null, link: null });
  };

  const renderDatePicker = () => {
    const { type, startDate, endDate, selectedDate } = datePickerState;

    return (
      <Dialog open={datePickerVisible}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {type === "range"
                ? "Select Date Range"
                : type === "single"
                  ? "Select Date"
                  : ""}
            </DialogTitle>
          </DialogHeader>
          {type === "range" && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Start Date
                </Label>
                <DatePicker
                  selected={startDate}
                  onSelect={(date) =>
                    setDatePickerState({
                      ...datePickerState,
                      startDate: date,
                    })
                  }
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="username" className="text-right">
                  End Date
                </Label>
                <DatePicker
                  selected={endDate}
                  onSelect={(date) =>
                    setDatePickerState({
                      ...datePickerState,
                      endDate: date,
                    })
                  }
                  minDate={startDate}
                />
              </div>
            </div>
          )}
          {type === "single" && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Start Date
                </Label>
                <DatePicker
                  selected={selectedDate}
                  onSelect={(date) =>
                    setDatePickerState({
                      ...datePickerState,
                      selectedDate: date,
                    })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={handleApplyDates}
            >
              Apply
            </button>
            <button
              type="button"
              className="ml-2 inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={() => {
                setDatePickerVisible(false);
                setDatePickerState({ type: null, link: null });
              }}
            >
              Cancel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // const renderHelpDrawer = () => (
  //   <Drawer
  //     open={helpDrawerVisible}
  //     onOpenChange={setHelpDrawerVisible}
  //     direction="right"
  //   >
  //     <DrawerContent className="focus-visible:outline-none w-fit min-w-[30vw] p-0">
  //       <DrawerClose asChild>
  //         <Button className="fixed inset-y-2 right-4 bg-white mt-4 hover:bg-white">
  //           <X className="h-5 w-5 text-gray-500" />
  //         </Button>
  //       </DrawerClose>
  //       <div className="pt-4">
  //         <h2 className="text-2xl font-bold text-wrap mb-8 mt-3 ml-4">
  //           Dynamic Reports Generation
  //         </h2>

  //         <div className="mt-">
  //           {props.reportsList ? (
  //             props.reportsList.map((report, index) => (
  //               <div
  //                 key={index}
  //                 className="w-full py-2 border-b border-gray-300 flex items-center"
  //               >
  //                 <ProportionsIcon className="h-4 w-4 mx-4 " />
  //                 <div className="flex-col">
  //                   <p className="text-sm text-gray-700 font-light">
  //                     {report.display_name}
  //                   </p>
  //                   <p className="text-xs text-gray-500 font-extralight">
  //                     {report.description}
  //                   </p>
  //                 </div>
  //                 <ChevronRight className="h-4 w-4 ml-auto mr-4" />
  //               </div>
  //             ))
  //           ) : (
  //             <p className="text-gray-500">Loading reports...</p>
  //           )}
  //           {props.reportsList && props.reportsList.length === 0 && (
  //             <p className="text-gray-500">No reports available.</p>
  //           )}
  //         </div>
  //       </div>
  //     </DrawerContent>
  //   </Drawer>
  // );

  return (
    <div className="h-fit flex self-stretch">
      <div className="flex flex-col flex-grow justify-start mb-6 w-full py-4">
        <p className="text-3xl font-bold text-w-full left">
          Search for Reports
        </p>
        <div className="mt-6 mb-4 flex w-full relative">
          <AutoComplete
            options={reportOptions}
            className="py-2 rounded-md"
            emptyMessage={
              searchReport?.length > 0
                ? "No reports found"
                : "Start typing to search for reports"
            }
            placeholder="Search for reports"
            onValueChange={handleValueChange}
            value={selectedReport}
            input={searchReport}
            onInputChanged={setSearchReport}
          />
          <Button
            variant="outline"
            onClick={() => setHelpDrawerVisible(true)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white border-none h-8 p-2"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="w-1 bg-[#d9d9d9] mx-4" />
      <div className="flex flex-col overflow-auto w-1/2">
        <p className="text-sm font-semibold mb-4">
          {" "}
          Quick Report Access
        </p>
        <div className="flex flex-col gap-4">
          <ReportAnchor
            title="Expiry List"
            link="/reports/expiry-list/"
            onClick={() =>
              handleReportAnchorClick("/branch/reports/expiry-list/", false, false)
            }
          />

          <ReportAnchor
            title="Void Sales"
            link=""
            onClick={() =>
              handleReportAnchorClick("/branch/sales_history?type=VOID", false, false)
            }
          />

          <ReportAnchor
            title="Refunded Sales"
            link=""
            onClick={() =>
              handleReportAnchorClick(
                "/branch/sales_history?type=FULLY_REFUNDED",
                false,
                false
              )
            }
          />


          <ReportAnchor
            title="Profit & Loss"
            link="/reports/pl-report"
            onClick={() =>
              handleReportAnchorClick("/branch/reports/pl-report", true, true)
            }
          />
          <ReportAnchor
            title="Stock Summary"
            link="/inventory/available-inventory"
            onClick={() =>
              handleReportAnchorClick(
                "/branch/inventory/available-inventory",
                false,
                false
              )
            }
          />
        </div>
      </div>

      {datePickerVisible && renderDatePicker()}
      {/* {helpDrawerVisible && renderHelpDrawer()} */}
    </div>
  );
};

export default ViewReports;
